import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import { NETWORK_CONFIG } from '@/utils/constants';
import {
  MessageType,
  GameStateUpdatePayload,
  RoundEndPayload,
  UnoDeclaredPayload,
  PlayerDisconnectedPayload,
  PlayerReplacedByAIPayload
} from '@/types/network';
import type { CardColor } from '@/types/card';
import type { Team } from '@/types/player';

/**
 * 接続状態
 */
type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/**
 * ルーム内プレイヤー情報
 */
interface RoomPlayer {
  id: string;
  name: string;
  isConnected: boolean;
  isAI?: boolean;
  disconnectedAt?: number;  // 切断時のタイムスタンプ
}

/**
 * ネットワークメッセージ
 */
interface NetworkMessage {
  type: MessageType;
  payload: any;
  timestamp: number;
}

/**
 * ラウンド結果（サーバーから受信）
 */
interface NetworkRoundResult {
  roundNumber: number;
  winner: Team;
  damageDealt: Record<string, number>;
  remainingCards: Record<string, number>;
  lifePoints: Record<string, number>;
}

/**
 * ネットワークストア状態
 */
export interface NetworkStoreState {
  // 接続状態
  socket: Socket | null;
  connectionStatus: ConnectionStatus;
  serverUrl: string;

  // ルーム情報
  roomId: string | null;
  playerId: string | null;  // サーバーから割り当てられるプレイヤーID
  isHost: boolean;
  playersInRoom: RoomPlayer[];

  // ゲーム状態（サーバーから受信）
  lastGameState: GameStateUpdatePayload | null;
  lastRoundResult: NetworkRoundResult | null;
  matchWinner: Team | null;

  // ネットワーク品質
  latency: number;
  lastPingTimestamp: number;

  // メッセージキュー（オフライン耐性）
  messageQueue: NetworkMessage[];

  // エラーハンドリング
  connectionError: string | null;
  reconnectAttempts: number;

  // アクション: 接続管理
  connect: (serverUrl?: string) => Promise<void>;
  disconnect: () => void;

  // アクション: ルーム管理
  createRoom: (playerName: string) => Promise<string>;
  joinRoom: (roomId: string, playerName: string) => Promise<void>;
  leaveRoom: () => void;
  startGame: () => void;

  // アクション: ゲームメッセージ
  sendPlayCard: (cardId: string, declaredColor?: CardColor) => void;
  sendDrawCard: () => void;
  sendDeclareUno: () => void;
  sendStartNextRound: () => void;

  // アクション: メッセージ処理
  handleIncomingMessage: (message: NetworkMessage) => void;
  flushMessageQueue: () => void;

  // アクション: ネットワーク品質
  measureLatency: () => void;
  startLatencyMonitoring: () => void;
  stopLatencyMonitoring: () => void;

  // アクション: クリーンアップ
  cleanup: () => void;
}

/**
 * レイテンシー監視インターバルID
 */
let latencyIntervalId: ReturnType<typeof setInterval> | null = null;

/**
 * ネットワークストア
 * Socket.io接続とオンラインマルチプレイヤー通信を管理
 */
export const useNetworkStore = create<NetworkStoreState>((set, get) => ({
  // 初期状態
  socket: null,
  connectionStatus: 'disconnected',
  serverUrl: NETWORK_CONFIG.DEFAULT_SERVER_URL,

  roomId: null,
  playerId: null,
  isHost: false,
  playersInRoom: [],

  lastGameState: null,
  lastRoundResult: null,
  matchWinner: null,

  latency: 0,
  lastPingTimestamp: 0,

  messageQueue: [],

  connectionError: null,
  reconnectAttempts: 0,

  /**
   * サーバーに接続
   * @param serverUrl サーバーURL（省略時はデフォルト）
   */
  connect: async (serverUrl?: string): Promise<void> => {
    const url = serverUrl || NETWORK_CONFIG.DEFAULT_SERVER_URL;

    set({
      connectionStatus: 'connecting',
      serverUrl: url,
      connectionError: null
    });

    console.log(`Connecting to server: ${url}`);

    try {
      const socket = io(url, {
        reconnection: true,
        reconnectionAttempts: NETWORK_CONFIG.RECONNECT_ATTEMPTS,
        reconnectionDelay: NETWORK_CONFIG.RECONNECT_DELAY,
        transports: ['websocket', 'polling']
      });

      // 接続成功イベント
      socket.on('connect', () => {
        console.log('Connected to server');
        set({
          socket,
          connectionStatus: 'connected',
          reconnectAttempts: 0,
          connectionError: null
        });

        // メッセージキューをフラッシュ
        get().flushMessageQueue();

        // レイテンシー監視開始
        get().startLatencyMonitoring();
      });

      // 切断イベント
      socket.on('disconnect', (reason) => {
        console.log('Disconnected from server:', reason);
        set({ connectionStatus: 'disconnected' });

        // レイテンシー監視停止
        get().stopLatencyMonitoring();
      });

      // 再接続試行イベント
      socket.on('reconnect_attempt', (attemptNumber) => {
        console.log(`Reconnecting... Attempt ${attemptNumber}`);
        set({
          connectionStatus: 'reconnecting',
          reconnectAttempts: attemptNumber
        });
      });

      // 再接続成功イベント
      socket.on('reconnect', (attemptNumber) => {
        console.log(`Reconnected after ${attemptNumber} attempts`);
        set({
          connectionStatus: 'connected',
          reconnectAttempts: 0
        });

        // メッセージキューをフラッシュ
        get().flushMessageQueue();
      });

      // 再接続失敗イベント
      socket.on('reconnect_failed', () => {
        console.error('Reconnection failed');
        set({
          connectionStatus: 'disconnected',
          connectionError: 'Failed to reconnect to server'
        });
      });

      // 接続エラーイベント
      socket.on('connect_error', (error) => {
        console.error('Connection error:', error);
        set({
          connectionError: error.message
        });
      });

      // ゲームメッセージハンドラ
      socket.on(MessageType.GAME_STATE_UPDATE, (payload: GameStateUpdatePayload) => {
        console.log('Received game state update:', payload.gameState.phase);

        const { lastGameState, playerId } = get();
        const isFirstGameState = lastGameState === null;

        // 状態を保存
        set({ lastGameState: payload });

        // gameStoreに同期（遅延インポートで循環参照を回避）
        import('./gameStore').then(({ useGameStore }) => {
          const gameStore = useGameStore.getState();

          // 非ホストプレイヤーの場合、最初のゲーム状態受信時にオンラインモードを初期化
          if (isFirstGameState && payload.gameState.phase === 'playing') {
            // オンラインモードを初期化（localPlayerIdを設定）
            gameStore.initializeOnlineMode(playerId?.toString() || '');
          }

          gameStore.syncFromNetwork(payload);
        });

        // 最初のゲーム状態受信時は画面遷移
        if (isFirstGameState && payload.gameState.phase === 'playing') {
          console.log('First game state received - navigating to game screen');
          import('./uiStore').then(({ useUIStore }) => {
            useUIStore.getState().navigateTo('game_board');
          });
        }
      });

      // ラウンド終了ハンドラ
      socket.on(MessageType.ROUND_END, (payload: RoundEndPayload) => {
        console.log(`Round ${payload.roundNumber} ended. Winner: ${payload.winner}`);

        const roundResult: NetworkRoundResult = {
          roundNumber: payload.roundNumber,
          winner: payload.winner,
          damageDealt: payload.damageDealt,
          remainingCards: payload.remainingCards,
          lifePoints: payload.lifePoints
        };

        set({ lastRoundResult: roundResult });

        // UIストアにラウンド結果を通知
        import('./uiStore').then(({ useUIStore }) => {
          useUIStore.getState().openRoundResult({
            roundNumber: payload.roundNumber,
            winner: payload.winner,
            damageDealt: payload.damageDealt,
            remainingCards: payload.remainingCards
          });
        });
      });

      // ゲーム終了ハンドラ
      socket.on(MessageType.GAME_END, (payload: { winner: Team }) => {
        console.log(`Game ended. Winner: ${payload.winner}`);

        set({ matchWinner: payload.winner });

        // UIストアにゲーム終了を通知
        // 注: RoundResultModal が開いている可能性があるので、先に閉じてから GameOver を開く
        // （タイミング遅延ではなく、イベント優先順位として即座にRoundResultを潰す）
        import('./uiStore').then(({ useUIStore }) => {
          // 開いている可能性がある RoundResultModal を即座に閉じる
          if (useUIStore.getState().modals.roundResult.isOpen) {
            useUIStore.getState().closeRoundResult();
          }
          useUIStore.getState().openGameOver(payload.winner);
        });
      });

      // UNO宣言ハンドラ
      socket.on(MessageType.UNO_DECLARED, (payload: UnoDeclaredPayload) => {
        console.log(`UNO declared by ${payload.playerName}`);

        // UNO宣言トースト表示
        import('./uiStore').then(({ useUIStore }) => {
          useUIStore.getState().showToast(
            `${payload.playerName} が UNO を宣言しました！`,
            'info'
          );
        });
      });

      // プレイヤー切断ハンドラ
      socket.on(MessageType.PLAYER_DISCONNECTED, (payload: PlayerDisconnectedPayload) => {
        console.log(`Player ${payload.playerId} disconnected. Reconnect timeout: ${payload.reconnectTimeout}ms`);

        // プレイヤーリストを更新（切断タイムスタンプを記録）
        set(state => ({
          playersInRoom: state.playersInRoom.map(p =>
            p.id === payload.playerId
              ? { ...p, isConnected: false, disconnectedAt: Date.now() }
              : p
          )
        }));
      });

      // プレイヤー再接続ハンドラ
      socket.on(MessageType.PLAYER_RECONNECTED, (payload: { playerId: string }) => {
        console.log(`Player ${payload.playerId} reconnected`);

        // プレイヤーリストを更新（切断タイムスタンプをクリア）
        set(state => ({
          playersInRoom: state.playersInRoom.map(p =>
            p.id === payload.playerId
              ? { ...p, isConnected: true, disconnectedAt: undefined }
              : p
          )
        }));
      });

      // プレイヤーAI置換ハンドラ
      socket.on(MessageType.PLAYER_REPLACED_BY_AI, (payload: PlayerReplacedByAIPayload) => {
        console.log(`Player ${payload.playerId} replaced by AI: ${payload.playerName}`);

        // プレイヤーリストを更新（AI化、切断タイムスタンプをクリア）
        set(state => ({
          playersInRoom: state.playersInRoom.map(p =>
            p.id === payload.playerId
              ? { ...p, name: payload.playerName, isConnected: false, isAI: true, disconnectedAt: undefined }
              : p
          )
        }));

        // トースト通知を表示
        import('./uiStore').then(({ useUIStore }) => {
          useUIStore.getState().showToast(
            `${payload.playerName} がAIに置き換えられました`,
            'info'
          );
        });
      });

      socket.on(MessageType.PLAYER_JOINED, (message: any) => {
        console.log('Player joined:', message.playerName);

        // プレイヤーリストを更新
        set(state => ({
          playersInRoom: [
            ...state.playersInRoom,
            {
              id: message.playerId,
              name: message.playerName,
              isConnected: true
            }
          ]
        }));
      });

      socket.on(MessageType.PLAYER_LEFT, (message: any) => {
        console.log('Player left:', message.playerId);

        // プレイヤーリストから削除
        set(state => ({
          playersInRoom: state.playersInRoom.filter(p => p.id !== message.playerId)
        }));
      });

      socket.on(MessageType.ROOM_CREATED, (message: any) => {
        console.log('Room created:', message.roomId);
        set({
          roomId: message.roomId,
          isHost: true
        });
      });

      socket.on(MessageType.JOINED_ROOM, (message: any) => {
        console.log('Joined room:', message.roomId);
        set({
          roomId: message.roomId,
          isHost: false,
          playersInRoom: message.players || []
        });
      });

      socket.on(MessageType.ERROR, (message: any) => {
        console.error('Server error:', message.message);
        set({ connectionError: message.message });
      });

      set({ socket });
    } catch (error) {
      console.error('Failed to connect:', error);
      set({
        connectionStatus: 'disconnected',
        connectionError: error instanceof Error ? error.message : 'Connection failed'
      });
      throw error;
    }
  },

  /**
   * サーバーから切断
   */
  disconnect: () => {
    const { socket } = get();

    if (socket) {
      console.log('Disconnecting from server');
      socket.disconnect();
      set({ socket: null, connectionStatus: 'disconnected' });
    }

    // レイテンシー監視停止
    get().stopLatencyMonitoring();
  },

  /**
   * ルームを作成
   * @param playerName プレイヤー名
   * @returns ルームID
   */
  createRoom: async (playerName: string): Promise<string> => {
    const { socket, connectionStatus } = get();

    if (connectionStatus !== 'connected' || !socket) {
      throw new Error('Not connected to server');
    }

    return new Promise((resolve, reject) => {
      socket.emit(MessageType.CREATE_ROOM, { playerName });

      socket.once(MessageType.ROOM_CREATED, (message: any) => {
        const roomId = message.roomId;
        const playerId = message.playerId || '1';  // ホストは通常Player 1
        console.log(`Room created: ${roomId}, playerId: ${playerId}`);

        set({
          roomId,
          playerId,
          isHost: true,
          playersInRoom: [{
            id: playerId,
            name: playerName,
            isConnected: true
          }]
        });

        resolve(roomId);
      });

      socket.once(MessageType.ERROR, (message: any) => {
        console.error('Failed to create room:', message.message);
        reject(new Error(message.message));
      });

      // タイムアウト
      setTimeout(() => {
        reject(new Error('Room creation timeout'));
      }, 10000);
    });
  },

  /**
   * ルームに参加
   * @param roomId ルームID
   * @param playerName プレイヤー名
   */
  joinRoom: async (roomId: string, playerName: string): Promise<void> => {
    const { socket, connectionStatus } = get();

    if (connectionStatus !== 'connected' || !socket) {
      throw new Error('Not connected to server');
    }

    return new Promise((resolve, reject) => {
      socket.emit(MessageType.JOIN_ROOM, { roomId, playerName });

      socket.once(MessageType.JOINED_ROOM, (message: any) => {
        const playerId = message.playerId;
        console.log(`Joined room: ${message.roomId}, playerId: ${playerId}`);

        set({
          roomId: message.roomId,
          playerId,
          isHost: false,
          playersInRoom: message.players || []
        });

        resolve();
      });

      socket.once(MessageType.ERROR, (message: any) => {
        console.error('Failed to join room:', message.message);
        reject(new Error(message.message));
      });

      // タイムアウト
      setTimeout(() => {
        reject(new Error('Room join timeout'));
      }, 10000);
    });
  },

  /**
   * ルームから退出
   */
  leaveRoom: () => {
    const { socket, roomId } = get();

    if (socket && roomId) {
      console.log(`Leaving room: ${roomId}`);
      socket.emit(MessageType.LEAVE_ROOM, { roomId });

      set({
        roomId: null,
        isHost: false,
        playersInRoom: []
      });
    }
  },

  /**
   * ゲーム開始（ホストのみ）
   */
  startGame: () => {
    const { socket, roomId, isHost } = get();

    if (!isHost) {
      console.error('Only host can start the game');
      return;
    }

    if (!socket || !roomId) {
      console.error('Not in a room');
      return;
    }

    console.log('Starting game');
    socket.emit(MessageType.GAME_START, { roomId });
  },

  /**
   * カードプレイメッセージを送信
   * @param cardId カードID
   * @param declaredColor 宣言する色
   */
  sendPlayCard: (cardId: string, declaredColor?: CardColor) => {
    const { socket, connectionStatus, roomId } = get();

    const message: NetworkMessage = {
      type: MessageType.PLAY_CARD,
      payload: { cardId, declaredColor, roomId },
      timestamp: Date.now()
    };

    if (connectionStatus === 'connected' && socket) {
      console.log('Sending play card:', cardId);
      socket.emit(MessageType.PLAY_CARD, message.payload);
    } else {
      console.warn('Offline - queuing play card message');
      set(state => ({
        messageQueue: [...state.messageQueue, message]
      }));
    }
  },

  /**
   * カードドローメッセージを送信
   */
  sendDrawCard: () => {
    const { socket, connectionStatus, roomId } = get();

    const message: NetworkMessage = {
      type: MessageType.DRAW_CARD,
      payload: { roomId },
      timestamp: Date.now()
    };

    if (connectionStatus === 'connected' && socket) {
      console.log('Sending draw card');
      socket.emit(MessageType.DRAW_CARD, message.payload);
    } else {
      console.warn('Offline - queuing draw card message');
      set(state => ({
        messageQueue: [...state.messageQueue, message]
      }));
    }
  },

  /**
   * UNO宣言メッセージを送信
   */
  sendDeclareUno: () => {
    const { socket, connectionStatus, roomId } = get();

    if (connectionStatus !== 'connected' || !socket) {
      console.error('Not connected to server');
      return;
    }

    if (!roomId) {
      console.error('Not in a room');
      return;
    }

    console.log('Sending UNO declaration');
    socket.emit(MessageType.DECLARE_UNO, { roomId });
  },

  /**
   * 次のラウンド開始メッセージを送信（ホストのみ）
   */
  sendStartNextRound: () => {
    const { socket, connectionStatus, roomId, isHost } = get();

    if (!isHost) {
      console.error('Only host can start next round');
      return;
    }

    if (connectionStatus !== 'connected' || !socket) {
      console.error('Not connected to server');
      return;
    }

    if (!roomId) {
      console.error('Not in a room');
      return;
    }

    console.log('Sending start next round');
    socket.emit(MessageType.START_NEXT_ROUND, { roomId });
  },

  /**
   * 受信メッセージを処理
   * @param message ネットワークメッセージ
   */
  handleIncomingMessage: (message: NetworkMessage) => {
    console.log('Handling message:', message.type);

    switch (message.type) {
      case MessageType.GAME_STATE_UPDATE:
        // ゲーム状態同期は既にイベントハンドラで処理済み
        break;

      case MessageType.PLAYER_JOINED:
      case MessageType.PLAYER_LEFT:
        // プレイヤーリスト更新は既にイベントハンドラで処理済み
        break;

      default:
        console.warn('Unknown message type:', message.type);
    }
  },

  /**
   * メッセージキューをフラッシュ
   */
  flushMessageQueue: () => {
    const { socket, messageQueue } = get();

    if (!socket || messageQueue.length === 0) {
      return;
    }

    console.log(`Flushing message queue: ${messageQueue.length} messages`);

    messageQueue.forEach(message => {
      socket.emit(message.type, message.payload);
    });

    set({ messageQueue: [] });
  },

  /**
   * レイテンシーを測定
   */
  measureLatency: () => {
    const { socket, connectionStatus } = get();

    if (connectionStatus !== 'connected' || !socket) {
      return;
    }

    const startTime = Date.now();

    socket.emit('ping', () => {
      const latency = Date.now() - startTime;
      set({
        latency,
        lastPingTimestamp: Date.now()
      });

      console.log(`Latency: ${latency}ms`);
    });
  },

  /**
   * レイテンシー監視を開始
   */
  startLatencyMonitoring: () => {
    // 既存の監視を停止
    get().stopLatencyMonitoring();

    // 5秒ごとにレイテンシーを測定
    latencyIntervalId = setInterval(() => {
      get().measureLatency();
    }, 5000);

    console.log('Latency monitoring started');
  },

  /**
   * レイテンシー監視を停止
   */
  stopLatencyMonitoring: () => {
    if (latencyIntervalId) {
      clearInterval(latencyIntervalId);
      latencyIntervalId = null;
      console.log('Latency monitoring stopped');
    }
  },

  /**
   * クリーンアップ
   */
  cleanup: () => {
    console.log('Cleaning up network store');

    // 接続を切断
    get().disconnect();

    // 状態をリセット
    set({
      socket: null,
      connectionStatus: 'disconnected',
      serverUrl: NETWORK_CONFIG.DEFAULT_SERVER_URL,
      roomId: null,
      playerId: null,
      isHost: false,
      playersInRoom: [],
      lastGameState: null,
      lastRoundResult: null,
      matchWinner: null,
      latency: 0,
      lastPingTimestamp: 0,
      messageQueue: [],
      connectionError: null,
      reconnectAttempts: 0
    });
  }
}));

// 型エクスポート
export type { ConnectionStatus, RoomPlayer, NetworkMessage, NetworkRoundResult };
